import { useEffect, useState } from 'react'
import { findDuplicates } from '../../services/duplicateService'
import type { DuplicateHit } from '../../services/duplicateService'
import { supabase } from '../../lib/supabase'
import { Deal, User, GstTreatment } from '../../types'
import { GST_TREATMENT_OPTIONS, GST_TREATMENT_HELP, treatmentOf } from '../../lib/gstTreatment'
import { lookupAbn, searchAbnByName, AbrResult } from '../../services/buyerService'
import ContactSavePrompt from '../ContactSavePrompt'
import type { AbrNameMatch } from '../../services/buyerService'
import { hasPermission } from '../../lib/permissions'
import { isAcquiredStatus } from '../../lib/dealLifecycle'
import { auditService } from '../../services/auditService'
import './DealEditPanel.css'
import { toast } from '../Toast'

/**
 * Correcting and archiving a deal.
 *
 * A deal, once created, could never be edited or removed. `deleted_at` existed
 * on the table but nothing ever wrote it — it was only ever read as a filter.
 * A typo'd VIN or a wrong purchase price was permanent, and a duplicate or
 * abandoned deal sat in the pipeline forever.
 *
 * Financially meaningful fields are locked once a deal has been paid, because
 * changing the purchase price after the money has gone out would silently
 * restate the margin, the commission and the closing-stock valuation.
 */

interface Props {
  deal: Deal
  user: User
  onSaved: (msg: string) => void
  onArchived: () => void
}

/*
 * Once the money has gone out, the purchase price is history: the GST register,
 * the closing-stock journal and the commission calculation have all used it.
 *
 * This was a hand-written list of four statuses, and it predated sold_in_transit,
 * sold_delivered and deal_pending. A car that had been sold and delivered was
 * therefore still editable - its purchase price could be changed after the
 * commission on it had been struck and the credit claimed.
 *
 * isAcquiredStatus is the canonical "we own it or we have sold it" test from
 * lib/dealLifecycle, so a stage added there locks this panel on the same deploy.
 */
const financialsLockedFor = (status: string | null | undefined) => isAcquiredStatus(status)

export default function DealEditPanel({ deal, user, onSaved, onArchived }: Props) {
  const financialsLocked = financialsLockedFor(deal.deal_status)
  const canArchive = hasPermission(user, 'perm-override-exceptions')

  const [form, setForm] = useState({
    vehicle_make: deal.vehicle_make || '',
    vehicle_model: deal.vehicle_model || '',
    vehicle_year: String(deal.vehicle_year ?? ''),
    vehicle_vin: deal.vehicle_vin || '',
    vehicle_odometer: String(deal.vehicle_odometer ?? ''),
    seller_name: deal.seller_name || '',
    seller_email: deal.seller_email || '',
    seller_phone: deal.seller_phone || '',
    seller_location: deal.seller_location || '',
    seller_type: (deal.seller_type || 'individual') as 'individual' | 'company' | 'dealer' | 'auction' | 'tender',
    valuation_amount: String(deal.valuation_amount ?? ''),
    registration_number: deal.registration_number || '',
    vehicle_condition: (deal.vehicle_condition || 'Used') as string,
    advertised_price: deal.advertised_price != null ? String(deal.advertised_price) : '',

    // Sales Workflow §4.4 — the field the margin scheme actually turns on.
    gst_treatment: treatmentOf(deal) as GstTreatment,

    // §4.1–4.3 vehicle and compliance detail
    registration_expiry: deal.registration_expiry || '',
    registration_state: deal.registration_state || '',
    stock_type: deal.stock_type || '',
    vehicle_series: deal.vehicle_series || '',
    vehicle_badge: deal.vehicle_badge || '',
    vehicle_body: deal.vehicle_body || '',
    interior_colour: deal.interior_colour || '',
    build_date: deal.build_date || '',
    compliance_date: deal.compliance_date || '',
    engine_number: deal.engine_number || '',
    odometer_unit: (deal.odometer_unit || 'km') as 'km' | 'mi',
    is_commercial: deal.is_commercial ? 'yes' : 'no',
    stock_group: deal.stock_group || '',

    seller_abn: deal.seller_abn || '',

    // §4.4 acquisition
    acquired_date: deal.acquired_date || '',
    acquired_method: deal.acquired_method || '',
    supplier_address: deal.supplier_address || '',

    notes: deal.notes || '',
  })

  const [abrBusy, setAbrBusy] = useState(false)
  const [abrNote, setAbrNote] = useState<string | null>(null)
  const [abrResult, setAbrResult] = useState<AbrResult | null>(null)
  const [abrSearchTerm, setAbrSearchTerm] = useState('')
  const [abrMatches, setAbrMatches] = useState<AbrNameMatch[]>([])
  const [abrSearchBusy, setAbrSearchBusy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dupes, setDupes] = useState<DuplicateHit[]>([])
  const [archiving, setArchiving] = useState(false)

  /*
   * Winnie's item 2: duplicate check points on VIN, rego, seller details and bank
   * account. Every one of these has a legitimate repeat - the same car returning, a
   * transferred plate, a good seller selling three cars - so this reports and never
   * blocks. Debounced, because it reads the deal book and nobody needs that on every
   * keystroke.
   */
  useEffect(() => {
    let live = true
    const t = window.setTimeout(() => {
      findDuplicates({
        vin: form.vehicle_vin,
        rego: form.registration_number,
        sellerName: form.seller_name,
        sellerEmail: form.seller_email,
        sellerMobile: form.seller_phone,
        sellerAbn: form.seller_abn,
        /* The bank details are not on this form - the seller enters them in the portal - so
           these read the values already stored on the deal rather than a field being typed.

           The check deliberately runs HERE and not in the seller portal. Telling a seller
           "this account is already on another deal" would leak the existence of somebody
           else's deal to an outside party. Staff can already see these details; the seller
           cannot see anyone's but their own. The value that comes back is masked either way. */
        bsb: deal.seller_bsb,
        accountNumber: deal.seller_account_number,
        excludeDealId: deal.id,
      })
        .then((hits) => { if (live) setDupes(hits) })
        .catch(() => { if (live) setDupes([]) })
    }, 500)
    return () => { live = false; window.clearTimeout(t) }
  }, [
    form.vehicle_vin, form.registration_number, form.seller_name, form.seller_email,
    form.seller_phone, form.seller_abn,
    deal.id, deal.seller_bsb, deal.seller_account_number,
  ])
  const [archiveReason, setArchiveReason] = useState('')
  const [confirmArchive, setConfirmArchive] = useState(false)

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const handleSave = async () => {
    setError(null)

    if (!form.seller_name.trim()) {
      setError('A seller name is required.')
      return
    }
    const year = form.vehicle_year ? parseInt(form.vehicle_year, 10) : null
    if (year !== null && (Number.isNaN(year) || year < 1900 || year > new Date().getFullYear() + 2)) {
      setError('That year does not look right.')
      return
    }
    const price = form.valuation_amount ? parseFloat(form.valuation_amount) : null
    if (price !== null && (Number.isNaN(price) || price < 0)) {
      setError('The purchase price must be a positive number.')
      return
    }

    setSaving(true)
    try {
      const before: Record<string, any> = {}
      const after: Record<string, any> = {}

      const advertised = form.advertised_price ? parseFloat(form.advertised_price) : null
      if (advertised !== null && (Number.isNaN(advertised) || advertised < 0)) {
        setError('The advertised price must be a positive number.')
        setSaving(false)
        return
      }

      const payload: Record<string, any> = {
        registration_number: form.registration_number.trim().toUpperCase() || null,
        vehicle_condition: form.vehicle_condition,
        advertised_price: advertised,
        vehicle_make: form.vehicle_make.trim() || null,
        vehicle_model: form.vehicle_model.trim() || null,
        vehicle_year: year,
        vehicle_vin: form.vehicle_vin.trim().toUpperCase() || null,
        vehicle_odometer: form.vehicle_odometer ? parseInt(form.vehicle_odometer, 10) : null,
        seller_name: form.seller_name.trim(),
        seller_email: form.seller_email.trim() || null,
        seller_phone: form.seller_phone.trim() || null,
        seller_location: form.seller_location.trim() || null,
        seller_type: form.seller_type,

        // §4.4. Recording this by hand makes it 'elected' — the register shows
        // which rows were only ever inferred from the seller type.
        seller_abn: form.seller_abn.replace(/\s/g, '') || null,
        gst_treatment: form.gst_treatment,
        gst_treatment_source:
          form.gst_treatment === treatmentOf(deal) && deal.gst_treatment_source === 'abr'
            ? 'abr'
            : 'elected',

        registration_expiry: form.registration_expiry || null,
        registration_state: form.registration_state.trim().toUpperCase() || null,
        stock_type: form.stock_type.trim() || null,
        vehicle_series: form.vehicle_series.trim() || null,
        vehicle_badge: form.vehicle_badge.trim() || null,
        vehicle_body: form.vehicle_body.trim() || null,
        interior_colour: form.interior_colour.trim() || null,
        build_date: form.build_date || null,
        compliance_date: form.compliance_date || null,
        engine_number: form.engine_number.trim().toUpperCase() || null,
        odometer_unit: form.odometer_unit,
        is_commercial: form.is_commercial === 'yes',
        stock_group: form.stock_group.trim() || null,
        acquired_date: form.acquired_date || null,
        acquired_method: form.acquired_method || null,
        supplier_address: form.supplier_address.trim() || null,

        notes: form.notes.trim() || null,
        updated_at: new Date().toISOString(),
      }

      // Only touch the price while it is still safe to.
      if (!financialsLocked && price !== null) payload.valuation_amount = price

      for (const [k, v] of Object.entries(payload)) {
        if (k === 'updated_at') continue
        const old = (deal as any)[k] ?? null
        if (String(old ?? '') !== String(v ?? '')) {
          before[k] = old
          after[k] = v
        }
      }

      if (Object.keys(after).length === 0) {
        onSaved('Nothing changed.')
        return
      }

      const { error: err } = await supabase.from('deals').update(payload).eq('id', deal.id)
      if (err) throw err

      await auditService.log(user, 'deal_edited', {
        details: { deal: deal.id, before, after },
      })

      toast.saved()
      onSaved(`Deal updated — ${Object.keys(after).length} field${Object.keys(after).length === 1 ? '' : 's'} changed.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the deal')
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    setError(null)
    if (!archiveReason.trim()) {
      setError('Give a reason — it is kept on the audit trail.')
      return
    }

    setArchiving(true)
    try {
      const { error: err } = await supabase
        .from('deals')
        .update({
          deleted_at: new Date().toISOString(),
          archived_reason: archiveReason.trim(),
          archived_by_user_id: user.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', deal.id)
      if (err) throw err

      await auditService.log(user, 'deal_archived', {
        details: { deal: deal.id, status_at_archive: deal.deal_status, reason: archiveReason.trim() },
      })

      onArchived()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not archive the deal')
      setArchiving(false)
    }
  }

  const field = (key: keyof typeof form, label: string, opts: { type?: string; disabled?: boolean; hint?: string } = {}) => (
    <div className="edit-field">
      <label htmlFor={`edit-${key}`}>{label}</label>
      <input
        id={`edit-${key}`}
        type={opts.type || 'text'}
        value={form[key]}
        disabled={opts.disabled}
        onChange={(e) => set(key, e.target.value)}
      />
      {opts.hint && <small className="edit-hint">{opts.hint}</small>}
    </div>
  )

  return (
    <div className="edit-panel">
      <div className="edit-head">
        <h4>Edit deal</h4>
        <p>
          Corrections are recorded field by field on the audit trail, with the before and after value.
        </p>
      </div>

      <div className="edit-grid">
        {field('vehicle_make', 'Make')}
        {field('vehicle_model', 'Model')}
        {field('vehicle_year', 'Year', { type: 'number' })}
        {field('vehicle_vin', 'VIN')}
        {field('registration_number', 'Rego No', { hint: 'Shown on the stock register.' })}
        {dupes.length > 0 && (
          <div className="edit-dupes">
            {dupes.map((h) => (
              <div key={h.kind + h.otherId} className="edit-dupe">{h.message}</div>
            ))}
          </div>
        )}
        <div className="edit-field">
          <label htmlFor="edit-vehicle_condition">New / Used</label>
          <select
            id="edit-vehicle_condition"
            value={form.vehicle_condition}
            onChange={(e) => set('vehicle_condition', e.target.value)}
          >
            <option value="Used">Used</option>
            <option value="New">New</option>
          </select>
        </div>
        {field('advertised_price', 'Advertised price', {
          type: 'number',
          hint: 'What the vehicle is listed at — separate from the Autograb valuation.',
        })}
        {field('vehicle_odometer', 'Odometer (km)', { type: 'number' })}
        {field('valuation_amount', 'Purchase price', {
          type: 'number',
          disabled: financialsLocked,
          hint: financialsLocked
            ? 'Locked — this deal has been paid. Changing it now would restate margin, commission and closing stock.'
            : undefined,
        })}
        {field('seller_name', 'Seller name')}
        {field('seller_email', 'Seller email', { type: 'email' })}
        {field('seller_phone', 'Seller phone')}
        {field('seller_location', 'Seller location')}
        <div className="edit-field">
          <label htmlFor="edit-seller_type">Seller type</label>
          <select
            id="edit-seller_type"
            value={form.seller_type}
            onChange={(e) => set('seller_type', e.target.value)}
          >
            <option value="individual">Type 1 — Private (Individual)</option>
            <option value="company">Type 2 — Private (Company)</option>
            <option value="dealer">Type 3 — LMCT / Dealer</option>
            <option value="auction">Type 4 — Auction</option>
            <option value="tender">Type 5 — Tender</option>
          </select>
          <small className="edit-hint">Drives the DD document set and the PPSR grantor.</small>
        </div>

        <div className="edit-field wide">
          <label htmlFor="edit-gst_treatment">GST treatment</label>
          <select
            id="edit-gst_treatment"
            value={form.gst_treatment}
            onChange={(e) => set('gst_treatment', e.target.value)}
            disabled={financialsLocked}
          >
            {GST_TREATMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="edit-abr">
            <input
              id="edit-seller_abn"
              value={form.seller_abn}
              onChange={(e) => set('seller_abn', e.target.value)}
              placeholder="Seller ABN"
              aria-label="Seller ABN"
            />
            <button
              type="button"
              className="edit-abr-btn"
              disabled={!form.seller_abn || abrBusy}
              onClick={async () => {
                setAbrNote(null)
                setAbrBusy(true)
                const r = await lookupAbn(form.seller_abn)
                setAbrBusy(false)
                setAbrResult(r)
                setAbrNote(r.message)
                if (r.ok) {
                  // The register is the authority on whether a seller is
                  // GST-registered, so it sets the treatment rather than
                  // suggesting it.
                  set('gst_treatment', r.gstRegistered ? 'tax_invoice' : 'notional_itc')
                  /*
                   * The register is also the authority on the legal entity name, which is
                   * what belongs on the contract. Filling it is the point of the check -
                   * leaving it for someone to retype by hand is how the name on a purchase
                   * agreement ends up not matching the name on the ABN.
                   */
                  if (r.legalName && r.legalName !== form.seller_name) {
                    set('seller_name', r.legalName)
                  }
                }
              }}
            >
              {abrBusy ? 'Checking…' : 'Check the ABR'}
            </button>
          </div>
          {abrNote && <small className="edit-hint">{abrNote}</small>}
          {/* Winnie, 15 Sep: offer to keep what the register just told us. */}
          <ContactSavePrompt
            abn={form.seller_abn}
            result={abrResult}
            type="Seller"
            suggest={{ name: form.seller_name, email: form.seller_email, phone: form.seller_phone }}
          />
          {/*
            * Winnie's Type 3 flow: the sales team is given a trading name, not an ABN.
            * Searching the register by name and picking a match fills the ABN in, then
            * the ABN check above fills the legal entity name. Two steps, because a
            * trading-name match returns the trading name and not the legal one.
            */}
          <div className="edit-abr">
            <input
              value={abrSearchTerm}
              onChange={(e) => setAbrSearchTerm(e.target.value)}
              placeholder="or search the register by trading name"
              aria-label="Search the ABR by business or trading name"
            />
            <button
              type="button"
              className="edit-abr-btn"
              disabled={abrSearchTerm.trim().length < 3 || abrSearchBusy}
              onClick={async () => {
                setAbrNote(null)
                setAbrMatches([])
                setAbrSearchBusy(true)
                const r = await searchAbnByName(abrSearchTerm)
                setAbrSearchBusy(false)
                setAbrNote(r.message)
                setAbrMatches(r.results)
              }}
            >
              {abrSearchBusy ? 'Searching…' : 'Search the ABR'}
            </button>
          </div>
          {abrMatches.length > 0 && (
            <ul className="edit-abr-matches">
              {abrMatches.map((m) => (
                <li key={m.abn}>
                  <button
                    type="button"
                    onClick={() => {
                      set('seller_abn', m.abn)
                      setAbrMatches([])
                      setAbrNote(`ABN filled in from ${m.name}. Check the ABR to pull the legal name.`)
                    }}
                  >
                    <strong>{m.name}</strong>
                    <span>
                      {m.abn}
                      {m.nameType ? ` · ${m.nameType}` : ''}
                      {m.state ? ` · ${m.state}` : ''}
                      {m.abnStatus && m.abnStatus !== 'Active' ? ` · ${m.abnStatus}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <small className="edit-hint">
            {GST_TREATMENT_HELP[form.gst_treatment]}
            {deal.gst_treatment_source === 'inferred' && (
              <>
                {' '}
                <strong>This deal's treatment was inferred from the seller type and has never
                been confirmed.</strong>
              </>
            )}
            {financialsLocked && ' Locked — the vehicle is paid for and the register has already used this.'}
          </small>
        </div>
      </div>

      <details className="edit-more">
        <summary>Vehicle, compliance and acquisition detail</summary>
        <div className="edit-grid">
          {field('registration_expiry', 'Rego expiry', { type: 'date' })}
          {field('registration_state', 'Rego state', { hint: 'VIC, NSW, QLD…' })}
          {field('stock_type', 'Stock type', { hint: 'Car, SUV, Truck…' })}
          {field('vehicle_series', 'Series')}
          {field('vehicle_badge', 'Badge', { hint: 'e.g. 45 TDI' })}
          {field('vehicle_body', 'Body', { hint: 'Wagon, Sedan…' })}
          {field('interior_colour', 'Interior colour')}
          {field('build_date', 'Build date', { type: 'date' })}
          {field('compliance_date', 'Compliance / RAV entry', { type: 'date' })}
          {field('engine_number', 'Engine number')}
          <div className="edit-field">
            <label htmlFor="edit-odometer_unit">Odometer unit</label>
            <select
              id="edit-odometer_unit"
              value={form.odometer_unit}
              onChange={(e) => set('odometer_unit', e.target.value)}
            >
              <option value="km">Kilometres</option>
              <option value="mi">Miles</option>
            </select>
          </div>
          <div className="edit-field">
            <label htmlFor="edit-is_commercial">Commercial vehicle</label>
            <select
              id="edit-is_commercial"
              value={form.is_commercial}
              onChange={(e) => set('is_commercial', e.target.value)}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          {field('stock_group', 'Stock group', { hint: 'e.g. Auction Purchases' })}
          {field('acquired_date', 'Acquired date', {
            type: 'date',
            hint: 'The date the vehicle was actually acquired. The GST register uses it in preference to the created date.',
          })}
          <div className="edit-field">
            <label htmlFor="edit-acquired_method">Acquired method</label>
            <select
              id="edit-acquired_method"
              value={form.acquired_method}
              onChange={(e) => set('acquired_method', e.target.value)}
            >
              <option value="">—</option>
              <option value="purchase">Purchase</option>
              <option value="auction">Auction</option>
              <option value="trade_in">Trade-in</option>
              <option value="consignment">Consignment</option>
            </select>
          </div>
          {field('supplier_address', 'Supplier address')}
        </div>
      </details>

      <div className="edit-field wide">
        <label htmlFor="edit-notes">Notes</label>
        <textarea id="edit-notes" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>

      {error && <div className="edit-alert">{error}</div>}

      <div className="edit-actions">
        <button className="edit-save" onClick={handleSave} disabled={saving || archiving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="edit-danger">
        <h5>Archive this deal</h5>
        <p>
          Archiving hides the deal from every pipeline, report and register. Nothing is deleted —
          the record and its history are retained permanently, per the framework's retention rule.
        </p>

        {!canArchive ? (
          <p className="edit-locked">
            Archiving needs the <strong>perm-override-exceptions</strong> permission.
          </p>
        ) : !confirmArchive ? (
          <button className="edit-archive" onClick={() => setConfirmArchive(true)} disabled={saving}>
            Archive deal…
          </button>
        ) : (
          <div className="edit-archive-confirm">
            <label htmlFor="archive-reason">Reason (required)</label>
            <input
              id="archive-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="e.g. duplicate of DEA10042, seller withdrew"
            />
            <div className="edit-archive-buttons">
              <button className="edit-cancel" onClick={() => setConfirmArchive(false)} disabled={archiving}>
                Keep the deal
              </button>
              <button className="edit-archive danger" onClick={handleArchive} disabled={archiving}>
                {archiving ? 'Archiving…' : 'Archive it'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
